import React from "react";
import { ArrowLeft } from "lucide-react";
import { DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { getCurrencySymbol } from "@/utils/currency";
import { STATUS_OPTIONS } from "../constants";
import {
  InfluencerSearchSelect,
  type InfluencerSearchItem,
} from "./InfluencerSearchSelect";

interface ExistingInfluencerFormProps {
  onBack: () => void;
  influencers: InfluencerSearchItem[];
  selectedInfluencerId: string;
  onSelectInfluencer: (id: string) => void;
  isLoadingInfluencers: boolean;
  isInfluencersError: boolean;
  emptyInfluencerMessage: string;
  influencerSearch: string;
  onInfluencerSearchChange: (value: string) => void;
  isDropdownOpen: boolean;
  onToggleDropdown: () => void;
  onOpenDropdown: () => void;
  status: string;
  onStatusChange: (status: string) => void;
  fee: string;
  onFeeChange: (fee: string) => void;
  userCountry?: string | null;
  deliverables: string;
  onDeliverablesChange: (deliverables: string) => void;
  notes: string;
  onNotesChange: (notes: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  onCancel: () => void;
  isPending: boolean;
}

export const ExistingInfluencerForm: React.FC<ExistingInfluencerFormProps> = ({
  onBack,
  influencers,
  selectedInfluencerId,
  onSelectInfluencer,
  isLoadingInfluencers,
  isInfluencersError,
  emptyInfluencerMessage,
  influencerSearch,
  onInfluencerSearchChange,
  isDropdownOpen,
  onToggleDropdown,
  onOpenDropdown,
  status,
  onStatusChange,
  fee,
  onFeeChange,
  userCountry,
  deliverables,
  onDeliverablesChange,
  notes,
  onNotesChange,
  onSubmit,
  onCancel,
  isPending,
}) => {
  return (
    <>
      <DialogHeader className="pb-2">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onBack}
            className="w-9 h-9 rounded-full border border-slate-200 hover:bg-slate-50 flex items-center justify-center text-slate-500 hover:text-slate-700 transition-colors focus:outline-none shrink-0"
            aria-label="Back"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <DialogTitle className="text-[20px] font-bold text-slate-900 leading-tight">
              Select Existing Influencer
            </DialogTitle>
            <p className="text-[13px] text-slate-500 font-normal mt-0.5">
              Pick from influencers already in your database
            </p>
          </div>
        </div>
      </DialogHeader>

      <form onSubmit={onSubmit} className="space-y-5">
        <InfluencerSearchSelect
          influencers={influencers}
          selectedInfluencerId={selectedInfluencerId}
          onSelectInfluencer={onSelectInfluencer}
          isLoading={isLoadingInfluencers}
          isError={isInfluencersError}
          search={influencerSearch}
          onSearchChange={onInfluencerSearchChange}
          isDropdownOpen={isDropdownOpen}
          onToggleDropdown={onToggleDropdown}
          onOpenDropdown={onOpenDropdown}
          emptyMessage={emptyInfluencerMessage}
        />

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="status">Status</Label>
            <Select value={status} onValueChange={onStatusChange}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="z-[200]">
                {STATUS_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="fee">
              Fee / Total Payment ({getCurrencySymbol(userCountry)})
            </Label>
            <Input
              id="fee"
              type="number"
              value={fee}
              onChange={(e) => onFeeChange(e.target.value)}
              placeholder="Enter amount"
            />
          </div>
        </div>

        <div>
          <Label htmlFor="deliverables">Deliverables</Label>
          <Input
            id="deliverables"
            value={deliverables}
            onChange={(e) => onDeliverablesChange(e.target.value)}
            placeholder="e.g., 1 Reel, 2 Stories, 1 Post"
          />
        </div>

        <div>
          <Label htmlFor="notes">Notes (Optional)</Label>
          <Textarea
            id="notes"
            value={notes}
            onChange={(e) => onNotesChange(e.target.value)}
            placeholder="Any additional notes or requirements..."
            rows={3}
          />
        </div>

        <div className="flex justify-end gap-3 pt-4">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" disabled={isPending}>
            {isPending ? "Adding..." : "Add Influencer"}
          </Button>
        </div>
      </form>
    </>
  );
};
